import { useState } from 'react';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  firstName: '', lastName: '', email: '', company: '',
  internalMailCode: '', streetAddress: '', division: '', department: '',
  buildingRoom: '', city: '', zipCode: '', location: '', province: '',
  application: '', species: '', otherSpecies: '', targetType: '', pcrType: '',
  accessionNumbers: '', intronExonJunctions: '', snpPosition: '',
  oligoNeeds: '', includeLNA: '', includeMGB: '', designMultiplex: '',
  existingPrimers: '', predesignedAssay: '', customConditions: '',
  otherArea: '', otherComments: '',
};

const ContactDetailed = () => {
  const [data, setData] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ['firstName', 'lastName', 'email', 'company', 'city', 'zipCode', 'location', 'application', 'species', 'targetType', 'pcrType'];
    const missing = required.filter((k) => !data[k]);
    if (missing.length) {
      toast.error(`Please fill required fields: ${missing.join(', ')}`);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
      });
      if (file) fd.append('file', file);
      await axios.post(`${API_URL}/contact/submit-detailed`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Your design request has been submitted!');
      setData(INITIAL);
      setFile(null);
      const el = document.getElementById('detailed-file');
      if (el) el.value = '';
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to submit request.');
    } finally {
      setLoading(false);
    }
  };

  const SelectField = ({ id, label, value, onChange, options, placeholder }) => (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-1" data-testid={`detailed-${id}`}>
          <SelectValue placeholder={placeholder || 'Select...'} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Detailed Primer &amp; Probe Design Request</h1>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Provide complete project details so our team can design the optimal assay for your application.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>How can we reach you?</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div><Label htmlFor="firstName">First Name *</Label>
                <Input id="firstName" value={data.firstName} onChange={(e) => update('firstName', e.target.value)} required className="mt-1" data-testid="detailed-firstName" /></div>
              <div><Label htmlFor="lastName">Last Name *</Label>
                <Input id="lastName" value={data.lastName} onChange={(e) => update('lastName', e.target.value)} required className="mt-1" data-testid="detailed-lastName" /></div>
              <div><Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={data.email} onChange={(e) => update('email', e.target.value)} required className="mt-1" data-testid="detailed-email" /></div>
              <div><Label htmlFor="company">Company / Institution *</Label>
                <Input id="company" value={data.company} onChange={(e) => update('company', e.target.value)} required className="mt-1" data-testid="detailed-company" /></div>
              <div><Label htmlFor="division">Division</Label>
                <Input id="division" value={data.division} onChange={(e) => update('division', e.target.value)} className="mt-1" /></div>
              <div><Label htmlFor="department">Department</Label>
                <Input id="department" value={data.department} onChange={(e) => update('department', e.target.value)} className="mt-1" /></div>
              <div><Label htmlFor="buildingRoom">Building / Room</Label>
                <Input id="buildingRoom" value={data.buildingRoom} onChange={(e) => update('buildingRoom', e.target.value)} className="mt-1" /></div>
              <div><Label htmlFor="internalMailCode">Internal Mail Code</Label>
                <Input id="internalMailCode" value={data.internalMailCode} onChange={(e) => update('internalMailCode', e.target.value)} className="mt-1" /></div>
              <div className="md:col-span-2"><Label htmlFor="streetAddress">Street Address</Label>
                <Input id="streetAddress" value={data.streetAddress} onChange={(e) => update('streetAddress', e.target.value)} className="mt-1" /></div>
              <div><Label htmlFor="city">City *</Label>
                <Input id="city" value={data.city} onChange={(e) => update('city', e.target.value)} required className="mt-1" data-testid="detailed-city" /></div>
              <div><Label htmlFor="zipCode">ZIP / Postal Code *</Label>
                <Input id="zipCode" value={data.zipCode} onChange={(e) => update('zipCode', e.target.value)} required className="mt-1" data-testid="detailed-zip" /></div>
              <div><Label htmlFor="province">Province / State</Label>
                <Input id="province" value={data.province} onChange={(e) => update('province', e.target.value)} className="mt-1" /></div>
              <div><Label htmlFor="location">Country *</Label>
                <Input id="location" value={data.location} onChange={(e) => update('location', e.target.value)} required className="mt-1" data-testid="detailed-country" /></div>
            </CardContent>
          </Card>

          {/* Design Inputs */}
          <Card>
            <CardHeader>
              <CardTitle>Design Inputs</CardTitle>
              <CardDescription>Tell us about your assay and target.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <SelectField id="application" label="Application *" value={data.application}
                onChange={(v) => update('application', v)}
                options={['Gene Expression', 'Genotyping (SNP)', 'Pathogen Detection', 'miRNA', 'Copy Number Variation', 'Methylation', 'Other']} />
              <SelectField id="species" label="Species *" value={data.species}
                onChange={(v) => update('species', v)}
                options={['Human', 'Mouse', 'Rat', 'Bovine', 'Plant', 'Bacteria', 'Virus', 'Other']} />
              {data.species === 'Other' && (
                <div className="md:col-span-2"><Label htmlFor="otherSpecies">Other Species (specify)</Label>
                  <Input id="otherSpecies" value={data.otherSpecies} onChange={(e) => update('otherSpecies', e.target.value)} className="mt-1" /></div>
              )}
              <SelectField id="targetType" label="Target Type *" value={data.targetType}
                onChange={(v) => update('targetType', v)}
                options={['DNA', 'mRNA', 'miRNA', 'lncRNA', 'rRNA', 'Plasmid']} />
              <SelectField id="pcrType" label="PCR Type *" value={data.pcrType}
                onChange={(v) => update('pcrType', v)}
                options={['Real-Time PCR (qPCR)', 'Digital PCR (dPCR)', 'Endpoint PCR', 'RT-PCR']} />
              <div className="md:col-span-2"><Label htmlFor="accessionNumbers">Accession Numbers / Target Sequences</Label>
                <Textarea id="accessionNumbers" value={data.accessionNumbers} onChange={(e) => update('accessionNumbers', e.target.value)} rows={3} className="mt-1" placeholder="e.g. NM_000546.5, ENSG00000141510" /></div>
              <div className="md:col-span-2"><Label htmlFor="intronExonJunctions">Intron / Exon Junctions</Label>
                <Textarea id="intronExonJunctions" value={data.intronExonJunctions} onChange={(e) => update('intronExonJunctions', e.target.value)} rows={2} className="mt-1" /></div>
              <div className="md:col-span-2"><Label htmlFor="snpPosition">SNP Position</Label>
                <Input id="snpPosition" value={data.snpPosition} onChange={(e) => update('snpPosition', e.target.value)} className="mt-1" /></div>
              <SelectField id="oligoNeeds" label="Oligo Needs" value={data.oligoNeeds}
                onChange={(v) => update('oligoNeeds', v)}
                options={['Primers only', 'Primers + Probe (TaqMan)', 'Primers + Probe (LNA)', 'Probe only']} />
              <SelectField id="includeLNA" label="Include LNA" value={data.includeLNA}
                onChange={(v) => update('includeLNA', v)} options={['Yes', 'No']} />
              <SelectField id="includeMGB" label="Include MGB-EDQ" value={data.includeMGB}
                onChange={(v) => update('includeMGB', v)} options={['Yes', 'No']} />
              <SelectField id="designMultiplex" label="Design for Multiplexing" value={data.designMultiplex}
                onChange={(v) => update('designMultiplex', v)} options={['Yes', 'No']} />
              <div className="md:col-span-2"><Label htmlFor="existingPrimers">Existing Primers</Label>
                <Textarea id="existingPrimers" value={data.existingPrimers} onChange={(e) => update('existingPrimers', e.target.value)} rows={2} className="mt-1" /></div>
              <div className="md:col-span-2"><Label htmlFor="predesignedAssay">Pre-designed Assay (catalog #, vendor)</Label>
                <Textarea id="predesignedAssay" value={data.predesignedAssay} onChange={(e) => update('predesignedAssay', e.target.value)} rows={2} className="mt-1" /></div>
              <div className="md:col-span-2"><Label htmlFor="customConditions">Custom Reaction Conditions</Label>
                <Textarea id="customConditions" value={data.customConditions} onChange={(e) => update('customConditions', e.target.value)} rows={2} className="mt-1" /></div>
              <div className="md:col-span-2"><Label htmlFor="otherArea">Other Area of Interest</Label>
                <Input id="otherArea" value={data.otherArea} onChange={(e) => update('otherArea', e.target.value)} className="mt-1" /></div>
              <div className="md:col-span-2"><Label htmlFor="otherComments">Other Comments</Label>
                <Textarea id="otherComments" value={data.otherComments} onChange={(e) => update('otherComments', e.target.value)} rows={3} className="mt-1" /></div>
            </CardContent>
          </Card>

          {/* Attachment */}
          <Card>
            <CardHeader>
              <CardTitle>Attachment (Optional)</CardTitle>
              <CardDescription>Attach FASTA, Excel, or PDF (max 5MB).</CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                id="detailed-file"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.fasta,.fa"
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (!f) return;
                  if (f.size > 5 * 1024 * 1024) {
                    toast.error('File must be ≤ 5MB');
                    e.target.value = '';
                    return;
                  }
                  setFile(f);
                  toast.success(`Attached: ${f.name}`);
                }}
                data-testid="detailed-file"
              />
            </CardContent>
          </Card>

          <Button
            type="submit"
            size="lg"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="detailed-submit"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <><Send className="w-4 h-4 mr-2" />Submit Design Request</>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default ContactDetailed;
